import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { RuleAction, ruleAction } from '@shared/models/site';
import { OrganizationSiteService, Rule, SiteDetail } from '@shared/services/organization-site.service';
import { ToastrService } from '@shared/services/toastr.service';
import { Unsubscribe } from '@shared/decorator/unsubscribe';
import _ from 'lodash';
import { ClusterConfig } from '@shared/services/organization-cluster.service';
import { HostModeService } from '@shared/services/hostMode.service';
import { DirtyStateService } from '@shared/services/dirty-state/dirty-state.service';
import { ClipboardService } from '@shared/services/clipboard.service';
import { HelpComponent } from '@shared/components/help/help.component';
import { NgIf } from '@angular/common';
import { TableGripComponent } from '@shared/components/table-grip/table-grip.component';
import { SwitchComponent } from '@shared/components/switch/switch.component';
import { FormsModule } from '@angular/forms';
import { RulesDetailsComponent } from './rules-details/rules-details.component';
import { TranslatePipe } from '@ngx-translate/core';
import { TranslateLabelPipe } from '@shared/pipe/filter.pipe';

@Unsubscribe
@Component({
  selector: 'app-rules',
  templateUrl: './rules.component.html',
  styleUrl: './rules.component.scss',
  imports: [
    HelpComponent,
    NgIf,
    CdkDropList,
    CdkDrag,
    TableGripComponent,
    SwitchComponent,
    FormsModule,
    RulesDetailsComponent,
    TranslatePipe,
    TranslateLabelPipe,
  ],
})
export class RulesComponent implements OnInit, OnDestroy {
  @Input({ required: true }) siteName: string;
  @Input({ required: true }) clusterConfig: Pick<ClusterConfig, 'supportsCache' | 'supportsChallenge'>;

  screenMode: 'LIST' | 'EDIT' | 'CREATE' = 'LIST';
  site: SiteDetail;
  disabled = true;
  editedRules: Rule[];
  displayedRules: Rule[];
  editMode = false;
  editedRule: Rule;
  editedIndex?: number;

  constructor(
    private siteApi: OrganizationSiteService,
    public hostModeService: HostModeService,
    private toastr: ToastrService,
    private clipboard: ClipboardService,
    protected dirtyState: DirtyStateService,
  ) {}

  ngOnInit(): void {
    this.loadSite();
  }

  ngOnDestroy(): void {
    this.dirtyState.clear();
  }

  toggleEditMode(): void {
    if (!this.editMode && this.disabled) {
      this.hostModeService.promptSubscribeToMode('SiteModeNotEnabled.advanced');
      return;
    }

    this.editMode = !this.editMode;
    if (this.editMode) {
      this.editedRules = _.cloneDeep(this.site.rules);
      this.displayedRules = this.editedRules;
    } else {
      this.dirtyState.clear();
    }
  }

  rollbackEdit() {
    this.endEdit();
  }

  commitEdit(): void {
    this.siteApi.patchSite(this.siteName, { rules: this.editedRules }).subscribe({
      next: (updatedSite) => {
        this.toastr.success('SiteModifieSucc');
        this.site = updatedSite;
        this.endEdit();
      },
    });
  }

  private endEdit() {
    this.editMode = false;
    this.editedRules = undefined;
    this.displayedRules = this.site.rules;
    this.dirtyState.clear();
  }

  drop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex != event.currentIndex) {
      this.dirtyState.setDirty();
      moveItemInArray(this.editedRules, event.previousIndex, event.currentIndex);
    }
  }

  prepareCreate(): void {
    this.editedRule = {
      active: true,
      action: 'brain',
      paths: [],
      defaultPolicy: 'allow',
      ipExceptions: [],
      challenge: 'disabled',
    };
    this.editedIndex = this.editedRules.length;
    this.dirtyState.setDirty();
    this.screenMode = 'CREATE';
  }

  prepareUpdate(index: number): void {
    this.editedRule = _.cloneDeep(this.editedRules[index]);
    this.editedIndex = index;
    this.screenMode = 'EDIT';
  }

  delete(rule: Rule): void {
    const index = this.editedRules.indexOf(rule);
    if (index > -1) this.editedRules.splice(index, 1);
    this.dirtyState.setDirty();
  }

  private loadSite(): void {
    this.siteApi.getSite(this.siteName).subscribe({
      next: (site: SiteDetail) => {
        this.site = site;
        this.displayedRules = site.rules;
        this.disabled = !this.hostModeService.isAdvanced(this.site.mode);
      },
      error: (err) => this.toastr.error(`${err}`, 'Failed to load site'),
    });
  }

  protected validateRule(rule: Rule): void {
    if (this.screenMode === 'CREATE') {
      this.editedRules.push(rule);
    } else if (this.screenMode === 'EDIT') {
      this.editedRules[this.editedIndex] = rule;
    }
    this.endEditRule();
  }

  private endEditRule() {
    this.editedRule = null;
    this.editedIndex = null;
    this.screenMode = 'LIST';
  }

  protected cancelEdit() {
    this.endEditRule();
  }

  protected readonly ruleAction: Record<RuleAction, string> = ruleAction;

  @HostListener('window:keydown', ['$event'])
  protected onKeyDown(event: KeyboardEvent) {
    if (this.screenMode === 'LIST') {
      if (this.editMode) {
        if (event.key === 'Escape') {
          this.rollbackEdit();
        } else if ((event.key === 'Enter' || event.key === 's') && event.ctrlKey) {
          event.preventDefault();
          event.stopPropagation();
          this.commitEdit();
        } else if (event.key === '+' || event.key === 'a') {
          event.preventDefault();
          event.stopPropagation();
          this.prepareCreate();
        }
      } else {
        if (event.key === 'e') {
          this.toggleEditMode();
        }
      }
    }
  }

  protected copyIpExceptionsToClipboard(ipExceptions: Array<string>) {
    this.clipboard.copy(ipExceptions.join(','), 'Rule.ipExceptions.copySuccess');
  }
}
